/**
 * The 5-minute enforcement sweep.
 *
 * Deletes every message and call-log row whose `expiresAt` has passed, from:
 *   - app start
 *   - a timer while a tab is open (SWEEP_INTERVAL_MS)
 *   - every visibilitychange back to visible
 *   - the service worker's Periodic Background Sync, where supported
 *
 * Limitation: with every tab closed and no background sync (Safari, Firefox),
 * the next sweep only happens on the next app open. This is inherent to the web
 * platform and is disclosed to the user.
 */
import { DELIVERED_TTL_MS, SWEEP_INTERVAL_MS } from "@truewave/protocol";
import { db } from "./db.js";

export interface SweepResult {
  messages: number;
  callLogs: number;
  /** Attachment/voice-note bodies deleted along with their message. */
  attachmentBlobs: number;
  at: number;
}

let listeners: Array<(r: SweepResult) => void> = [];
let timer: ReturnType<typeof setInterval> | null = null;
let onVisible: (() => void) | null = null;

export function onSweep(fn: (r: SweepResult) => void): () => void {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}

export async function sweepNow(now: number = Date.now()): Promise<SweepResult> {
  // Collect the ids first: the stored bodies (photos, voice notes) live in a
  // separate table keyed by message id, and nothing else deletes them once
  // their message is gone — an unopened attachment used to leave its encrypted
  // body behind for good.
  const expiredIds = (await db.messages.where("expiresAt").belowOrEqual(now).primaryKeys()) as string[];
  const [messages, callLogs] = await Promise.all([
    expiredIds.length ? db.messages.bulkDelete(expiredIds).then(() => expiredIds.length) : Promise.resolve(0),
    db.callLogs.where("expiresAt").belowOrEqual(now).delete(),
  ]);
  const attachmentBlobs = await deleteOrphanBlobs(expiredIds);
  await clearStaleNotifications(now);
  const result: SweepResult = { messages, callLogs, attachmentBlobs, at: now };
  if (messages > 0 || callLogs > 0 || attachmentBlobs > 0) for (const l of listeners) l(result);
  return result;
}

/** Bodies whose message row is gone — the ones just expired, plus any strays. */
async function deleteOrphanBlobs(justExpired: string[]): Promise<number> {
  const ids = (await db.attachmentBlobs.toCollection().primaryKeys()) as string[];
  if (ids.length === 0) return 0;
  const expired = new Set(justExpired);
  const rows = await db.messages.bulkGet(ids);
  const orphans = ids.filter((id, i) => expired.has(id) || rows[i] === undefined);
  if (orphans.length === 0) return 0;
  await db.attachmentBlobs.bulkDelete(orphans);
  return orphans.length;
}

async function clearStaleNotifications(now: number): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (!reg) return;
    const notes = await reg.getNotifications();
    for (const n of notes) {
      const shownAt = typeof n.data?.shownAt === "number" ? n.data.shownAt : 0;
      if (now - shownAt > DELIVERED_TTL_MS) n.close();
    }
  } catch {
    /* notifications not available */
  }
}

export function startSweeper(): void {
  void sweepNow();
  timer ??= setInterval(() => void sweepNow(), SWEEP_INTERVAL_MS);

  if (!onVisible) {
    onVisible = () => {
      if (document.visibilityState === "visible") void sweepNow();
    };
    document.addEventListener("visibilitychange", onVisible);
  }

  void registerPeriodicSweep();
}

export function stopSweeper(): void {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  if (onVisible) {
    document.removeEventListener("visibilitychange", onVisible);
    onVisible = null;
  }
}

async function registerPeriodicSweep(): Promise<void> {
  try {
    const reg = (await navigator.serviceWorker?.ready) as
      | (ServiceWorkerRegistration & { periodicSync?: { register: (tag: string, o: object) => Promise<void> } })
      | undefined;
    const status = await navigator.permissions
      ?.query({ name: "periodic-background-sync" as PermissionName })
      .catch(() => undefined);
    if (reg?.periodicSync && status?.state === "granted") {
      await reg.periodicSync.register("tw-sweep", { minInterval: 15 * 60 * 1000 });
    }
  } catch {
    /* periodic sync unsupported — timer + on-open sweeps still apply */
  }
}

/** Milliseconds until a message self-destructs, or null if the timer hasn't started. */
export function timeLeft(expiresAt: number | null, now: number = Date.now()): number | null {
  if (expiresAt === null) return null;
  return Math.max(0, expiresAt - now);
}
